import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentPosts();
  }, []);

  const fetchRecentPosts = async () => {
    try {
      const postsQuery = query(
        collection(db, 'blog-posts'),
        where('status', '==', 'published'),
        orderBy('createdAt', 'desc'),
        limit(4)
      );
      const snapshot = await getDocs(postsQuery);
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Don't render section if no posts
  if (!loading && posts.length === 0) {
    return null;
  }

  return (
    <section className="bg-[#FFF5F2] py-20 px-6">
      <div className="max-w-screen-xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-[0.3em] text-gray-400 mb-2">
            Latest from Our Blog
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-gray-900">
            Gift Ideas & Inspiration
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="text-gray-600">Loading posts...</div>
          </div>
        ) : (
          <>
            {/* Blog Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow group"
                >
                  {/* Blog Image */}
                  {post.featuredImage && (
                    <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  )}

                  {/* Blog Content */}
                  <div className="p-6">
                    {/* Author & Date */}
                    <div className="flex items-center gap-3 text-xs mb-3">
                      <span className="text-[#E85D44] font-semibold uppercase tracking-wider">
                        {post.author?.name}
                      </span>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-400 uppercase tracking-wider">
                        {formatDate(post.createdAt)}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-serif text-gray-900 leading-tight mb-3 group-hover:text-[#E85D44] transition-colors">
                      {post.title}
                    </h3>

                    {/* Excerpt */}
                    {post.excerpt && (
                      <p className="text-gray-600 text-sm line-clamp-3">
                        {post.excerpt}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>

            {/* Read Full Blog Button */}
            <div className="text-center">
              <Link
                to="/blog"
                className="inline-block bg-transparent border-2 border-gray-300 hover:border-[#E85D44] hover:bg-[#E85D44] text-gray-700 hover:text-white font-semibold text-sm uppercase tracking-[0.2em] px-10 py-4 transition-all duration-300"
              >
                READ FULL BLOG
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
